import cn from 'classnames';
import React from 'react';
import type { OptionProps } from 'react-select';
import ReactSelect from 'react-select';
import styles from './select.module.scss';

export interface SelectOption {
  label: string;
  value: any;
  [key: string]: any;
}

interface NativeSelectProps {
  handleChange: (tab: string) => void;
  options: Array<SelectOption>;
  value: string;
  className?: string;
  id?: string;
}

interface DropdownSelectProps {
  value?: SelectOption | null;
  options: Array<SelectOption>;
  onChange: (event: any) => void;
  className?: string;
  displayFlags?: boolean;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  styles?: any;
}

export type SelectProps = NativeSelectProps | DropdownSelectProps;

const disabledSelectStyles = {
  border: '1px solid #DADDE2',
  borderColor: '#DADDE2',
  background: 'rgba(33, 35, 37, 0.05)',
  color: '#7A808A'
};

const selectStyles = {
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    transition: 'all .2s ease',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
    paddingRight: '12px',
    svg: { width: '24px', height: '24px' }
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  control: (base: any, state: any) => ({
    ...base,
    border: '1px solid #B5BDBD',
    borderRadius: 0,
    height: '48px',
    paddingLeft: '6px',
    borderColor: '#333',
    boxShadow: 0,
    ':hover': {
      borderColor: '#B5BDBD',
      boxShadow: 0
    }
  }),
  menu: (provided: any) => ({
    ...provided,
    width: '100%',
    boxShadow: 'none',
    border: '1px solid #B5BDBD',
    borderRadius: 0,
    top: '40px',
    position: 'absolute',
    color: '#212833',
    padding: 0
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    padding: '16px',
    backgroundColor: '#fff',
    fontWeight: state.isSelected ? '700' : '400',
    color: '#212833',
    ':hover': {
      backgroundColor: '#EDEDED'
    }
  })
};

const mergeDisabledSelectStyles = (customStyles?: any) => {
  const baseStyles = customStyles || selectStyles;

  return {
    ...baseStyles,
    control: (base: any, state: any) => {
      const controlStyles = baseStyles.control
        ? baseStyles.control(base, state)
        : base;

      if (!state.isDisabled) {
        return controlStyles;
      }

      return {
        ...controlStyles,
        ...disabledSelectStyles,
        boxShadow: 0,
        cursor: 'not-allowed',
        ':hover': {
          ...controlStyles[':hover'],
          border: disabledSelectStyles.border,
          borderColor: disabledSelectStyles.borderColor,
          boxShadow: 0
        }
      };
    },
    dropdownIndicator: (base: any, state: any) => {
      const dropdownIndicatorStyles = baseStyles.dropdownIndicator
        ? baseStyles.dropdownIndicator(base, state)
        : base;

      return state.isDisabled
        ? { ...dropdownIndicatorStyles, color: disabledSelectStyles.color }
        : dropdownIndicatorStyles;
    },
    input: (base: any, state: any) => {
      const inputStyles = baseStyles.input ? baseStyles.input(base, state) : base;

      return state.isDisabled
        ? { ...inputStyles, color: disabledSelectStyles.color }
        : inputStyles;
    },
    placeholder: (base: any, state: any) => {
      const placeholderStyles = baseStyles.placeholder
        ? baseStyles.placeholder(base, state)
        : base;

      return state.isDisabled
        ? { ...placeholderStyles, color: disabledSelectStyles.color }
        : placeholderStyles;
    },
    singleValue: (base: any, state: any) => {
      const singleValueStyles = baseStyles.singleValue
        ? baseStyles.singleValue(base, state)
        : base;

      return state.isDisabled
        ? { ...singleValueStyles, color: disabledSelectStyles.color }
        : singleValueStyles;
    }
  };
};

const getFlagUrl = (countryFlag: any) => {
  if (!countryFlag) {
    return undefined;
  }

  if (Array.isArray(countryFlag)) {
    const [flag] = countryFlag;

    return flag?.url;
  }

  if (typeof countryFlag === 'string') {
    return countryFlag;
  }

  return countryFlag.url;
};

const getCountryFlag = (data: SelectOption) => {
  const value = data.value || {};

  if (value.countryFlag) {
    return value.countryFlag;
  }

  if (data.countryFlag) {
    return data.countryFlag;
  }

  return data.flag;
};

const Option = ({ innerProps, data, label }: OptionProps<SelectOption>) => {
  const flagUrl = getFlagUrl(getCountryFlag(data));

  return (
    <div className={styles.option} {...innerProps}>
      {flagUrl && (
        <span className={styles.flag}>
          <img src={flagUrl} alt={label} />
        </span>
      )}
      {label}
    </div>
  );
};

const NativeSelect: React.FC<NativeSelectProps> = ({
  options = [],
  handleChange,
  value,
  className,
  id
}) => {
  const selectedOption = options.find(
    (option) => option.label === value || option.value === value
  );
  const selectedValue = selectedOption ? selectedOption.value : '';

  const changeTab = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange(evt.target.value);
  };

  return (
    <div className={cn(styles.root, styles.native, className)} data-testid={id}>
      <h2> {value} </h2>
      <select onChange={changeTab} value={selectedValue}>
        {options.map((option) => (
          <option value={option.value} key={`option-${option.value}`}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const DropdownSelect: React.FC<DropdownSelectProps> = ({
  value,
  options,
  onChange,
  className,
  displayFlags = false,
  placeholder,
  disabled = false,
  id = 'select',
  styles: customStyles
}) => {
  const customComponents = displayFlags ? { Option } : {};

  return (
    <div className={cn(styles.root, className)} data-testid={id}>
      <ReactSelect
        value={value}
        options={options}
        onChange={onChange}
        styles={mergeDisabledSelectStyles(customStyles)}
        placeholder={placeholder}
        isDisabled={disabled}
        components={customComponents}
        classNamePrefix="select"
      />
    </div>
  );
};

const Select: React.FC<SelectProps> = (props) => {
  if ('handleChange' in props) {
    return <NativeSelect {...props} />;
  }

  return <DropdownSelect {...props} />;
};

export default Select;
